import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, usd, formatApiErrorDetail, CARD_PAYMENTS_ENABLED } from "../lib/api";
import { useRegistration } from "../context/RegistrationContext";
import Navbar from "../components/Navbar";
import { TermsCheckbox } from "../components/TermsCheckbox";
import { PaymentMethodCard, OrderDetailsCard, CampSidebar } from "../components/checkout/CheckoutParts";
import { ShieldCheck } from "lucide-react";

const toPayload = (p) => ({
  first_name: p.first_name, last_name: p.last_name, dob: p.dob, gender: p.gender,
  tshirt_size: p.tshirt_size, shorts_size: p.shorts_size, socks_size: p.socks_size,
  medical: { none: p.medical_none, details: p.medical_details },
  meal_dietary: p.meal_dietary,
  services: p.services,
});

function submitLabel(submitting, method, total) {
  if (submitting) return "Processing...";
  if (method === "stripe") return `Pay ${usd(total)} with Card`;
  return "Confirm & Get Bank Details";
}

function EmptyCheckout({ onStart }) {
  return (
    <div><Navbar />
      <div className="max-w-xl mx-auto px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-[#03281B]">No registration in progress</h1>
        <button onClick={onStart} className="mt-6 rounded-full bg-[#008057] text-white px-6 py-2.5 font-bold">Start Registration</button>
      </div>
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const { config, participants, guardian, consents, giftRecipientIndex, quote } = useRegistration();
  const [method, setMethod] = useState(CARD_PAYMENTS_ENABLED ? "stripe" : "bank");
  const [submitting, setSubmitting] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  if (!quote || !quote.total) return <EmptyCheckout onStart={() => navigate("/register")} />;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", {
        guardian, consents, participants: participants.map(toPayload),
        referral_code: guardian.referral_code || null,
        payment_method: method, gift_recipient_index: giftRecipientIndex,
        origin_url: window.location.origin,
        terms_accepted_at: new Date().toISOString(),
      });
      if (method !== "stripe") { toast.success("Registration created!"); navigate(`/confirmation/${data.reference}`); return; }
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.error("Unable to start Stripe checkout.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const label = submitLabel(submitting, method, quote.total);

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#03281B] tracking-tight">Checkout</h1>
        <p className="text-slate-600 mt-1">Review your order and choose a payment method.</p>
        <div className="grid lg:grid-cols-5 gap-8 mt-8">
          <div className="lg:col-span-3 space-y-6">
            <OrderDetailsCard quote={quote} participants={participants} />
            <PaymentMethodCard method={method} setMethod={setMethod} bank={config?.bank_details || {}} />
            <TermsCheckbox id="accept-terms" testid="accept-terms-checkbox" termsTestid="checkout-terms-link" privacyTestid="checkout-privacy-link" checked={acceptedTerms} onChange={setAcceptedTerms} />
            <button data-testid="submit-registration-btn" onClick={submit} disabled={submitting || !acceptedTerms} className="w-full rounded-full bg-[#008057] hover:bg-[#054D34] disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 font-bold text-lg transition-colors flex items-center justify-center gap-2">
              <ShieldCheck size={20} /> {label}
            </button>
            {CARD_PAYMENTS_ENABLED && <p className="text-center text-xs text-slate-400">Your card details are never stored by us. Payments are processed securely by Stripe.</p>}
          </div>
          <div className="lg:col-span-2"><CampSidebar quote={quote} count={participants.length} /></div>
        </div>
      </div>
    </div>
  );
}
